import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { userDataService } from '../services/UserDataService';
import { useStreamAuth } from '../hooks/useStreamAuth';
import { useWatchlist } from '../hooks/useWatchlist';
import { useLanguage } from '../hooks/useLanguage';
import GlobalPreviewPlayer from './GlobalPreviewPlayer';
import { getDirectVideoUrl } from '../utils/VideoUrlService';
import { optimizeImageUrl, getResponsiveImagePreset } from '../utils/ImageUtils';
import { useGlobalMutedState } from '../hooks/useGlobalMutedState';
import ProgressBar from './common/ProgressBar';
import './ThumbnailHoverModal.css';
import spotlightService from '../services/SpotlightService';
import { episodeSelectionService } from '../services/EpisodeSelectionService';
import ReactionButtons from './ReactionButtons';
import { APP_CONFIG } from '../config/AppConfig';
import { isEffectivelyTVODLocked, lockedChildProductId } from '../utils/pdpAccess';

const ThumbnailHoverModal = React.memo(
  ({
    channel,
    isVisible,
    position,
    onMouseEnter,
    onMouseLeave,
    onOpenPDPModal,
    preCalculatedContinueWatching = [],
    preCalculatedWatchAgain = [],
  }) => {
    const { loginWithRedirect } = useAuth0();
    const { strings } = useLanguage();
    const [showVideo, setShowVideo] = useState(false);
    const [isInWatchlist, setIsInWatchlist] = useState(false);
    const [isAddingToWatchlist, setIsAddingToWatchlist] = useState(false);
    const [shouldRender, setShouldRender] = useState(isVisible);
    const [exiting, setExiting] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [showTooltip, setShowTooltip] = useState(false);
    const [videoLoaded, setVideoLoaded] = useState(false);
    
    // Subscription/Paywall state
    const [subscriptionData, setSubscriptionData] = useState(null);
    const [isCheckingSubscription, setIsCheckingSubscription] = useState(false);
    const [productData, setProductData] = useState(null);

    // Modal dimensions for dynamic positioning
    const [modalDimensions, setModalDimensions] = useState({ width: 541, height: 400 });

    const modalRef = useRef(null);
    const previewTimerRef = useRef(null);
    const lastStartTimeRef = useRef(null);
    const navigate = useNavigate();
    const { isAuthenticated } = useStreamAuth();
    const { isMuted, toggleMuted } = useGlobalMutedState();

    // Use the useWatchlist hook to get reactive watchlist data
    const {
      watchlistData,
      isInWatchlist: hookIsInWatchlist,
      addToWatchlist: hookAddToWatchlist,
      removeFromWatchlist: hookRemoveFromWatchlist,
    } = useWatchlist();

    // Preview timing constants
    const HOVER_DELAY = 300; // Delay before starting preview (ms)
    const VIDEO_DELAY = 700; // Longer delay before video starts (700ms vs 300ms)
    const PREVIEW_DURATION = 60000; // Maximum preview duration (60 seconds)

    // Reset subscription state when channel changes or modal closes
    useEffect(() => {
      if (!isVisible) {
        // Reset state when modal closes
        setSubscriptionData(null);
        setProductData(null);
        setIsCheckingSubscription(false);
      }
    }, [isVisible]);

    // Reset subscription state when channel changes
    useEffect(() => {
      setSubscriptionData(null);
      setProductData(null);
      setIsCheckingSubscription(false);
    }, [channel?._id]);

    // Check subscription access when channel loads - matching PDPModal logic for TVOD support
    useEffect(() => {
      if (!isVisible || !channel?._id) return;

      // Check if we have complete subscription_access with accurate purchasable field
      // If ads_enabled is true but purchasable is false/undefined, we need to verify with API
      // because search results may have incomplete data
      const hasAccurateSubscriptionAccess = channel.subscription_access && 
        (channel.subscription_access.purchasable === true || 
         channel.subscription_access.ads_enabled !== true);

      if (hasAccurateSubscriptionAccess) {
        // Use the subscription_access - either purchasable is true, or content is not ad-gated
        console.log('💳 ThumbnailHover: Using subscription_access from appload:', {
          channelId: channel._id,
          title: channel.title,
          unlocked: channel.subscription_access.unlocked,
          ads_enabled: channel.subscription_access.ads_enabled,
          purchasable: channel.subscription_access.purchasable
        });
        setSubscriptionData(channel.subscription_access);
        return;
      }

      // Need to check API - either no subscription_access, or ad-gated but purchasable not confirmed
      const checkSubscriptionAccess = async () => {
        // If content has ads_enabled but purchasable is NOT explicitly true, we MUST call API
        // because the local data might have purchasable: false or undefined when it should be true
        const hasAdsButPurchasableNotConfirmed = channel.subscription_access?.ads_enabled === true && 
          channel.subscription_access?.purchasable !== true;

        // Only skip API check if is_product is explicitly false AND content is not ad-gated
        if (channel.is_product === false && !hasAdsButPurchasableNotConfirmed) {
          setSubscriptionData({ unlocked: true, ads_enabled: false, purchasable: false });
          return;
        }

        console.log('💳 ThumbnailHover: Checking subscription via API for:', channel.title);
        setIsCheckingSubscription(true);
        try {
          const productId = channel.dspro_id || channel._id;
          const subscriptionAccess = await spotlightService.checkSubscriptionAccess(productId);
          console.log('💳 ThumbnailHover: API subscription result:', {
            channelId: channel._id,
            title: channel.title,
            ...subscriptionAccess
          });
          setSubscriptionData(subscriptionAccess);
        } catch (error) {
          console.error('💳 ThumbnailHover: Failed to check subscription:', error);
          // Default to unlocked on error
          setSubscriptionData({ unlocked: true, ads_enabled: false, purchasable: false });
        } finally {
          setIsCheckingSubscription(false);
        }
      };

      checkSubscriptionAccess();
    }, [channel?._id, channel?.subscription_access, channel?.is_product, channel?.dspro_id, isVisible]);

    // Fetch product data when content is purchasable (for pricing display)
    // This applies to both ad-gated (unlocked + ads + purchasable) and TVOD locked (!unlocked + purchasable)
    useEffect(() => {
      if (!isVisible || !subscriptionData?.purchasable) return;
      
      const fetchProductData = async () => {
        // Use subscriptionData.dspro_id first (from API), then channel data
        const productId = subscriptionData?.dspro_id || channel?.dspro_id || channel?._id;
        
        if (!productId) {
          console.warn('💳 ThumbnailHover: No product ID available for purchasable content');
          return;
        }

        try {
          console.log('💳 ThumbnailHover: Fetching product data for:', channel?.title);
          const productsResponse = await spotlightService.getProductsByChannel(productId);
          const products = productsResponse?.products || [];
          if (products.length > 0) {
            // Get the first/primary product
            const primaryProduct = products[0];
            setProductData({
              price: primaryProduct.price,
              title: primaryProduct.title,
              id: primaryProduct._id
            });
            console.log('💳 ThumbnailHover: Product data loaded:', primaryProduct);
          }
        } catch (error) {
          console.error('💳 ThumbnailHover: Error fetching product data:', error);
        }
      };

      fetchProductData();
    }, [isVisible, subscriptionData?.purchasable, subscriptionData?.dspro_id, channel?.dspro_id, channel?._id, channel?.title]);

    // Get pre-calculated hover modal data
    const hoverData = useMemo(() => {
      if (!channel?._id) return null;
      return userDataService.getHoverModalData(channel._id);
    }, [channel?._id, preCalculatedContinueWatching, preCalculatedWatchAgain]); // Recalculate when watch progress changes

    // Extract data from pre-calculated hover data
    const {
      videoId: preCalculatedVideoId,
      startTime: preCalculatedStartTime,
      resumptionData: watchProgress,
      isMovie: preCalculatedIsMovie,
      isSeries: preCalculatedIsSeries,
    } = hoverData || {};

    // Double-check content type detection in case pre-calculation was wrong
    const actualIsMovie = useMemo(() => {
      const hasChildChannels = channel?.childchannels && channel.childchannels.length > 0;
      const isProgrammingTypeSeries = channel?.programming_type === 'Series';
      const isTypeSeries = channel?.type === 'series';
      const isContentTypeSeries = channel?.content_type === 'series';
      const isProgrammingTypeMovie = channel?.programming_type === 'Movie';
      const isTypeMovie = channel?.type === 'movie';
      const isContentTypeMovie = channel?.content_type === 'movie';

      const isSeries =
        hasChildChannels || isProgrammingTypeSeries || isTypeSeries || isContentTypeSeries;
      const isMovie =
        isProgrammingTypeMovie ||
        isTypeMovie ||
        isContentTypeMovie ||
        (!isSeries && !hasChildChannels);

      // Log if there's a mismatch between pre-calculated and actual content type
      if (preCalculatedIsMovie !== isMovie || preCalculatedIsSeries !== isSeries) {
        // ThumbnailHover: Content type mismatch detected
      }

      return isMovie;
    }, [channel, preCalculatedIsMovie, preCalculatedIsSeries]);

    const actualIsSeries = !actualIsMovie;

    // Fetch full series details if needed (when pre-calculated data doesn't have video ID for series)
    const [seriesDetails, setSeriesDetails] = useState(null);
    const [isLoadingSeriesDetails, setIsLoadingSeriesDetails] = useState(false);

    // Fetch full movie details if needed (when pre-calculated data doesn't have duration for movies)
    const [movieDetails, setMovieDetails] = useState(null);
    const [isLoadingMovieDetails, setIsLoadingMovieDetails] = useState(false);

    useEffect(() => {
      // Only fetch details when modal is visible and we need them
      if (!isVisible) return;

      const fetchSeriesDetails = async () => {
        // Only fetch if this is a series and we don't have a video ID from pre-calculated data
        if (
          actualIsSeries &&
          !preCalculatedVideoId &&
          channel?.slug &&
          !seriesDetails &&
          !isLoadingSeriesDetails
        ) {
          // ThumbnailHover: Fetching full series details for video ID
          setIsLoadingSeriesDetails(true);

          try {
            const response = await spotlightService.getChannelBySlug(channel.slug);
            if (response?.channels?.[0]?.childchannels?.length > 0) {
              const firstSeason = response.channels[0].childchannels[0];
              if (firstSeason?.playlist?.length > 0) {
                const firstEpisode = firstSeason.playlist[0];
                setSeriesDetails({
                  videoId: firstEpisode._id,
                  duration: firstEpisode.video?.duration || firstEpisode.duration || 0,
                });
                // ThumbnailHover: Fetched series details
              }
            }
          } catch (error) {
            console.error('ThumbnailHover: Error fetching series details:', error);
          } finally {
            setIsLoadingSeriesDetails(false);
          }
        }
      };

      const fetchMovieDetails = async () => {
        // Only fetch if this is a movie and we don't have duration from pre-calculated data
        if (
          actualIsMovie &&
          !preCalculatedStartTime &&
          channel?.slug &&
          !movieDetails &&
          !isLoadingMovieDetails
        ) {
          // ThumbnailHover: Fetching full movie details for duration
          setIsLoadingMovieDetails(true);

          try {
            const response = await spotlightService.getChannelBySlug(channel.slug);
            if (response?.channels?.[0]) {
              const movie = response.channels[0];
              const duration = movie.video?.duration || movie.duration || 0;
              if (duration) {
                setMovieDetails({
                  videoId: movie.video_id || movie.video?._id,
                  duration: duration,
                });
                // ThumbnailHover: Fetched movie details
              }
            }
          } catch (error) {
            console.error('ThumbnailHover: Error fetching movie details:', error);
          } finally {
            setIsLoadingMovieDetails(false);
          }
        }
      };

      fetchSeriesDetails();
      fetchMovieDetails();
    }, [
      isVisible, // Only run when modal becomes visible
      actualIsSeries,
      actualIsMovie,
      preCalculatedVideoId,
      preCalculatedStartTime,
      channel?.slug,
      // Remove these dependencies to prevent unnecessary re-runs
      // seriesDetails,
      // isLoadingSeriesDetails,
      // movieDetails,
      // isLoadingMovieDetails,
    ]);

    // Use ref to track the initial videoId to prevent re-renders
    const videoIdRef = useRef(null);

    // Determine final video ID (pre-calculated or fetched)
    const videoId = useMemo(() => {
      // Prioritize pre-calculated video ID from UserDataService (this should have the correct episode ID)

      if (preCalculatedVideoId) {
        if (videoIdRef.current !== preCalculatedVideoId) {
          // ThumbnailHover: Using pre-calculated video ID
          videoIdRef.current = preCalculatedVideoId;
        }
        return preCalculatedVideoId;
      }

      // For continue watching items without pre-calculated data, use the resumption _id as the video ID
      if (channel?.resumption_data && (channel.video_id || channel._id)) {
        const id = channel.video_id || channel._id;
        if (videoIdRef.current !== id) {
          // ThumbnailHover: Using resumption _id as video ID for continue watching
          videoIdRef.current = id;
        }
        return id;
      }

      // Only use fetched video ID if we don't have a pre-calculated one
      const fetchedVideoId = seriesDetails?.videoId || movieDetails?.videoId;
      if (fetchedVideoId && videoIdRef.current !== fetchedVideoId) {
        // ThumbnailHover: Using fetched video ID
        videoIdRef.current = fetchedVideoId;
        return fetchedVideoId;
      }

      // Return the current ref value to maintain stability
      return videoIdRef.current;
    }, [
      preCalculatedVideoId,
      channel?.resumption_data,
      channel?._id,
      // Only depend on fetched data if we don't have pre-calculated data
      ...(preCalculatedVideoId ? [] : [seriesDetails?.videoId, movieDetails?.videoId]),
    ]);

    // Calculate initial start time with stable dependencies only
    const initialStartTime = useMemo(() => {
      // For continue watching items, prioritize the resumption point from channel.resumption_data
      if (channel?.resumption_data?.point) {
        return channel.resumption_data.point;
      }

      // If we have resumption data from pre-calculation, use that start time
      if (watchProgress && watchProgress.point) {
        // ThumbnailHover: Using resumption point for start time
        return watchProgress.point;
      }

      // If we have a pre-calculated start time > 0, use that
      if (preCalculatedStartTime && preCalculatedStartTime > 0) {
        // ThumbnailHover: Using pre-calculated start time
        return preCalculatedStartTime;
      }

      // If no duration available, start from beginning
      // ThumbnailHover: No duration available, starting from beginning
      return 0;
    }, [
      channel?.resumption_data?.point,
      watchProgress?.point,
      preCalculatedStartTime,
      // Only depend on stable data that doesn't change during async operations
    ]);

    // State for final start time that can be updated when we get fetched data
    const [finalStartTime, setFinalStartTime] = useState(initialStartTime);

    // Update final start time when we get fetched data
    useEffect(() => {
      if (initialStartTime > 0) {
        // If we have a good initial start time, use it
        setFinalStartTime(initialStartTime);
        return;
      }

      // If we have a pre-calculated start time > 0, use that
      if (preCalculatedStartTime > 0) {
        // ThumbnailHover: Using pre-calculated start time
        setFinalStartTime(preCalculatedStartTime);
        return;
      }

      // Calculate 20% fallback from available duration data (including when metadata loads)
      const isMovie = preCalculatedIsMovie !== undefined ? preCalculatedIsMovie : actualIsMovie;
      const isSeries = preCalculatedIsSeries !== undefined ? preCalculatedIsSeries : actualIsSeries;

      let duration = 0;

      if (isMovie) {
        // For movies, try multiple duration sources
        // First try fetched movie details
        if (movieDetails?.duration) {
          duration = movieDetails.duration;
          const fallbackTime = Math.floor(Number(duration) * 0.2);
          setFinalStartTime(fallbackTime);
          return;
        }

        // Then try channel data if available
        duration = channel?.video?.duration || channel?.duration || 0;
        if (duration) {
          const fallbackTime = Math.floor(Number(duration) * 0.2);
          setFinalStartTime(fallbackTime);
          return;
        }
      } else if (isSeries) {
        // For series, try multiple duration sources
        // First try fetched series details
        if (seriesDetails?.duration) {
          duration = seriesDetails.duration;
          const fallbackTime = Math.floor(Number(duration) * 0.2);
          setFinalStartTime(fallbackTime);
          return;
        }

        // Then try channel data if available
        if (channel?.childchannels && channel.childchannels.length > 0) {
          const firstSeason = channel.childchannels[0];
          if (firstSeason?.playlist && firstSeason.playlist.length > 0) {
            const firstEpisode = firstSeason.playlist[0];
            duration = firstEpisode?.video?.duration || firstEpisode?.duration || 0;
            if (duration) {
              const fallbackTime = Math.floor(Number(duration) * 0.2);
              setFinalStartTime(fallbackTime);
              return;
            }
          }
        }
      }

      // If no duration available, start from beginning
      setFinalStartTime(0);
    }, [
      initialStartTime,
      preCalculatedIsMovie,
      preCalculatedIsSeries,
      actualIsMovie,
      actualIsSeries,
      movieDetails?.duration,
      seriesDetails?.duration,
      channel?.video?.duration,
      channel?.duration,
      channel?.childchannels,
    ]);

    // Use finalStartTime as the startTime
    const startTime = finalStartTime;

    // Extract channel data
    const title = channel?.title || 'Untitled';
    const description = channel?.description || 'No description available';
    const rating = channel?.rating || channel?.content_rating || '';
    const language = channel?.languageCode?.toUpperCase() || channel?.language || '';
    const country = channel?.country || '';
    const year = APP_CONFIG.FEATURES.SHOW_CONTENT_YEAR ? channel?.year || '' : '';
    const duration = channel?.duration || '';
    const genres = channel?.genres || [];
    const programType = channel?.programming_type || '';

    // ResizeObserver for dynamic modal sizing
    useEffect(() => {
      if (!modalRef.current) return;

      const resizeObserver = new ResizeObserver(entries => {
        const entry = entries[0];
        if (entry) {
          const { width, height } = entry.contentRect;
          setModalDimensions({ width, height });
        }
      });

      resizeObserver.observe(modalRef.current);

      return () => {
        resizeObserver.disconnect();
      };
    }, []);

    // Get optimized image URL - prioritize spotlight_poster for better quality
    const getImageUrl = () => {
      const rawImageUrl =
        channel?.spotlight_poster ||
        channel?.poster ||
        channel?.thumb ||
        channel?.image?.spotlight_poster ||
        channel?.image?.poster ||
        channel?.image?.thumb;

      if (!rawImageUrl) return '/placeholder-image.jpg';

      return optimizeImageUrl(rawImageUrl, getResponsiveImagePreset('HOVER_MODAL'));
    };

    // Format series episode info Netflix-style: "S1:E1 'Episode Title'"
    const formatSeriesEpisodeInfo = episodeData => {
      if (!episodeData || !actualIsSeries) return null;

      // Extract season and episode numbers from the episode data
      let seasonNumber = episodeData.season_number || 1;
      let episodeNumber = episodeData.episode_number || 1;

      // If not available, try to parse from title or other fields
      if (!episodeData.season_number || !episodeData.episode_number) {
        // Try to extract from title like "S1E1" or "Season 1 Episode 1"
        const titleMatch = episodeData.title?.match(
          /S(\d+)E(\d+)|Season\s+(\d+).*Episode\s+(\d+)/i,
        );
        if (titleMatch) {
          seasonNumber = parseInt(titleMatch[1] || titleMatch[3]) || 1;
          episodeNumber = parseInt(titleMatch[2] || titleMatch[4]) || 1;
        }
      }

      // Clean episode title (remove season/episode prefixes if present)
      let cleanTitle = episodeData.title || 'Episode';
      cleanTitle = cleanTitle.replace(/^S\d+E\d+\s*[-:]?\s*/i, '');
      cleanTitle = cleanTitle.replace(/^Season\s+\d+.*Episode\s+\d+\s*[-:]?\s*/i, '');

      return `S${seasonNumber}:E${episodeNumber} "${cleanTitle}"`;
    };

    // Enhanced series watch button logic that considers unwatched episodes
    const getSeriesWatchButtonInfo = channel => {
      if (!channel || !isAuthenticated) {
        return {
          text: strings.watchNow,
          className: 'modal-watch-button',
          icon: 'play',
        };
      }

      try {
        if (channel.resumption_data) {
          if (channel.resumption_data.point === 0) {
            // Assume this is a Next Episode resumption point,
            // as this value should not be 0 otherwise
            return {
              text: strings.continueWatching,
              className: 'modal-watch-button resume',
              icon: 'resume',
            };
          } else if (channel.resumption_data.progress < 1) {
            // We have progress on this asset but it's unfinished
            return {
              text: strings.resume,
              className: 'modal-watch-button resume',
              icon: 'resume',
            };
          } else if (channel.resumption_data.progress === 1) {
            // We have finished this series, since this should only
            // appear if we have no other assets to watch
            return {
              text: strings.watchAgain,
              className: 'modal-watch-button watch-again',
              icon: 'replay',
            };
          }
        }

        return {
          text: strings.watchNow,
          className: 'modal-watch-button',
          icon: 'play',
        };
      } catch (error) {
        console.error('ThumbnailHover: Error in series watch button logic:', error);
        return {
          text: strings.watchNow,
          className: 'modal-watch-button',
          icon: 'play',
        };
      }
    };

    // Smart button logic based on watch progress
    const getWatchButtonInfo = () => {
      const { unlocked, ads_enabled, purchasable } = subscriptionData || {};
      
      // TVOD locked content - must purchase to watch
      if (unlocked === false && purchasable === true) {
        const priceText = productData?.price ? ` - ${productData.price}` : '';
        return {
          text: `${strings.buyNow}${priceText}`,
          className: 'modal-buy-button',
          icon: 'lock',
        };
      }
      
      // Locked but not purchasable - content unavailable
      if (unlocked === false && !purchasable) {
        return {
          text: 'Not Available',
          className: 'modal-unavailable-button',
          icon: 'lock',
        };
      }

      // Parent wrapper whose seasons are all TVOD locked (ICOD bundles attach
      // to child channels) - the /subscriptions/check response carries the
      // locked childchannels even when the parent itself reports unlocked
      if (isEffectivelyTVODLocked(subscriptionData)) {
        const priceText = productData?.price ? ` - ${productData.price}` : '';
        return {
          text: `${strings.buyNow}${priceText}`,
          className: 'modal-buy-button',
          icon: 'lock',
        };
      }

      // Enhanced logic for series vs movies
      const isMovie = preCalculatedIsMovie !== undefined ? preCalculatedIsMovie : actualIsMovie;
      const isSeries = preCalculatedIsSeries !== undefined ? preCalculatedIsSeries : actualIsSeries;

      // For series, use the enhanced series logic
      if (isSeries) {
        return getSeriesWatchButtonInfo(channel);
      }

      // For movies, use the existing logic
      // Get current resumption data directly from UserDataService for accurate button state
      let progressData = null;

      if (channel?._id) {
        // For continue watching items, use the resumption data directly
        if (channel?.resumption_data) {
          progressData = channel.resumption_data;
        } else {
          // For regular channels, get the latest resumption data from UserDataService
          const latestResumption = userDataService.getLatestResumption(channel._id);
          if (latestResumption) {
            progressData = {
              progress: latestResumption.progress,
              point: latestResumption.point,
              duration: latestResumption.duration,
            };
          }
        }
      }

      // Fallback to pre-calculated data if no current data available
      if (!progressData) {
        progressData = watchProgress;
      }

      if (!progressData) {
        return {
          text: strings.watchNow,
          className: 'modal-watch-button',
          icon: 'play',
        };
      }

      // Check if content is completed (100% or very close to it)
      const isCompleted = progressData.progress >= 0.95; // 95% or more is considered completed

      if (isCompleted) {
        // For movies, completed means "Watch Again"
        return {
          text: strings.watchAgain,
          className: 'modal-watch-button watch-again',
          icon: 'replay',
        };
      } else {
        return {
          text: strings.resume,
          className: 'modal-watch-button resume',
          icon: 'resume',
        };
      }
    };

    const watchButtonInfo = getWatchButtonInfo();

    // Fetch signed preview URL asynchronously when videoId changes
    const [fetchedPreviewUrl, setFetchedPreviewUrl] = useState(null);
    useEffect(() => {
      if (!videoId) { setFetchedPreviewUrl(null); return; }
      let cancelled = false;
      getDirectVideoUrl(videoId).then(url => {
        if (!cancelled) setFetchedPreviewUrl(url);
      });
      return () => { cancelled = true; };
    }, [videoId]);

    // Get position class for modal placement
    const getPositionClass = () => {
      if (!position) return '';

      const { x, y } = position;
      const { width, height } = modalDimensions;

      // Check if modal would overflow right edge
      const wouldOverflowRight = x + width > window.innerWidth;
      // Check if modal would overflow bottom edge
      const wouldOverflowBottom = y + height > window.innerHeight;

      let positionClass = '';

      if (wouldOverflowRight) {
        positionClass += ' position-left';
      } else {
        positionClass += ' position-right';
      }

      if (wouldOverflowBottom) {
        positionClass += ' position-top';
      } else {
        positionClass += ' position-bottom';
      }

      return positionClass;
    };

    const positionClass = getPositionClass();

    // Generate preview URL when video should be shown
    useEffect(() => {
      if (!showVideo || !fetchedPreviewUrl) return;

      // Reset video loaded state when URL changes
      setVideoLoaded(false);

      // Add additional delay before setting preview URL for smoother transition
      const urlTimer = setTimeout(() => {
        setPreviewUrl(fetchedPreviewUrl);
      }, VIDEO_DELAY - HOVER_DELAY); // Adjust for initial hover delay

      // Set timer to stop preview after duration
      previewTimerRef.current = setTimeout(() => {
        setShowVideo(false);
        setPreviewUrl(null);
        setVideoLoaded(false);
      }, PREVIEW_DURATION);

      return () => {
        clearTimeout(urlTimer);
        if (previewTimerRef.current) {
          clearTimeout(previewTimerRef.current);
          previewTimerRef.current = null;
        }
      };
    }, [showVideo, fetchedPreviewUrl]); // Remove VIDEO_DELAY and HOVER_DELAY dependencies

  // Handle watch now click
  const handleWatchNow = () => {
    // If content is locked (purchasable) - directly or via all-locked seasons -
    // handle purchase flow
    if (isEffectivelyTVODLocked(subscriptionData)) {
      // If not authenticated, redirect to login first, then back to this PDPModal
      if (!isAuthenticated) {
        const channelSlug = channel?.slug || channel?.spotlight_slug;
        const returnUrl = channelSlug ? `/?channel=${channelSlug}` : window.location.pathname;
        console.log('🔐 ThumbnailHoverModal: Buy Now - redirecting to login with returnTo:', returnUrl);
        loginWithRedirect({
          appState: {
            returnTo: returnUrl,
          },
        });
        return;
      }
      
      // User is authenticated, navigate to checkout. When the lock lives on a
      // season (parent wrapper), buy that season's product, not the parent
      const channelId =
        subscriptionData?.unlocked === false
          ? channel?.dspro_id || channel?._id
          : lockedChildProductId(subscriptionData) || channel?.dspro_id || channel?._id;
      if (channelId) {
        navigate(`/checkout/${channelId}`, { state: { channel } });
      } else {
        console.warn('No channel ID available for checkout navigation');
      }
      return;
    }

    // Otherwise, navigate to watch page
    if (channel?.slug) {
      navigate(`/watch/${channel.slug}`);
    } else {
      console.warn('No channel slug available for watch navigation');
    }
  };

    // Handle volume toggle
    const handleVolumeToggle = () => {
      toggleMuted();
    };

    // Handle video ready event
    const handleVideoReady = () => {
      setVideoLoaded(true);

      // Add a small delay to ensure the video is actually playing before fading in
      setTimeout(() => {
        const videoElement = document.querySelector('.modal-video video');
        const fallbackThumbnail = document.querySelector('.fallback-thumbnail');

        if (videoElement) {
          videoElement.classList.add('playing');
        }

        if (fallbackThumbnail) {
          fallbackThumbnail.classList.add('fade-out');
        }
      }, 100);
    };

    // Handle video error event
    const handleVideoError = error => {
      setShowVideo(false);
      setPreviewUrl(null);
      setVideoLoaded(false);

      // Remove playing class on error
      const videoElement = document.querySelector('.modal-video video');
      if (videoElement) {
        videoElement.classList.remove('playing');
      }
    };

    // Clean up video fade-in effect when video stops
    useEffect(() => {
      if (!showVideo) {
        const videoElement = document.querySelector('.modal-video video');
        if (videoElement) {
          videoElement.classList.remove('playing');
        }
      }
    }, [showVideo]);

    // Handle add/remove from watchlist
    const handleWatchlistToggle = async () => {
      if (!isAuthenticated) {
        // Redirect to login if not authenticated
        loginWithRedirect();
        return;
      }

      // Debug: Log channel object to understand the ID structure
      console.log('🎬 ThumbnailHoverModal: Channel debug for watchlist:', {
        channel_id: channel?._id,
        dspro_id: channel?.dspro_id,
        contentId: channel?.contentId,
        title: channel?.title
      });
      
      // For continue watching items, we need to use the content ID, not the resumption ID
      let channelId = channel?._id;
      let parentChannelId = null;

      // If this is a continue watching item, use the content ID for watchlist operations
      if (channel?.contentId) {
        // For watchlist operations, we should use the actual channel._id, not contentId
        // The contentId is used for reactions, but watchlist needs the real channel ID
        // channelId = channel.contentId; // OLD - this causes 404
        channelId = channel._id; // NEW - use the actual channel ID
        // For series in continue watching, the contentId is the parent channel ID
        if (channel?.resumption_data?.parent_channel) {
          // This is a series episode, so use parent channel ID for watchlist operations
          parentChannelId = null; // No parent needed since contentId is already the parent
        }
      } else if (channel?.resumption_data) {
        // Fallback: If this is a continue watching item (has resumption_data), use the appropriate channel ID
        if (channel.resumption_data.parent_channel) {
          channelId = channel.resumption_data.parent_channel._id;
        } else if (channel.resumption_data.channel) {
          channelId = channel.resumption_data.channel._id;
        }
      } else if (channel?.childchannels && channel.childchannels.length > 0) {
        // This is a series from AppLoad with childchannels
        // Use the first child channel as the channelId and the parent as parentChannelId
        channelId = channel.childchannels[0]._id;
        parentChannelId = channel._id;
      }

      if (!channelId) {
        console.error('No valid channel ID available for watchlist operation');
        return;
      }

      setIsAddingToWatchlist(true);

      try {
        if (isInWatchlist) {
          // Remove from watchlist using hook method
          await hookRemoveFromWatchlist(channelId);
          // The hook will automatically update the watchlist data, which will trigger the useEffect above
        } else {
          // Add to watchlist using hook method
          await hookAddToWatchlist(channelId, parentChannelId);
          // The hook will automatically update the watchlist data, which will trigger the useEffect above
        }
      } catch (error) {
        console.error('Error toggling watchlist:', error);
        alert('There was an error updating your watchlist. Please try again.');
      } finally {
        setIsAddingToWatchlist(false);
      }
    };

    // Check watchlist status using reactive watchlist data
    useEffect(() => {
      if (isAuthenticated && channel?._id && isVisible) {
        try {
          // For continue watching items, we need to use the content ID, not the resumption ID
          let channelId = channel._id;
          let parentChannelId = null;

          // If this is a continue watching item, use the same ID logic as the add operation
          if (channel?.contentId) {
            // For watchlist operations, we should use the actual channel._id, not contentId
            // The contentId is used for reactions, but watchlist needs the real channel ID
            channelId = channel._id; // Use the actual channel ID (same as add operation)
            // For series in continue watching, the contentId is the parent channel ID
            if (channel?.resumption_data?.parent_channel) {
              // This is a series episode, so use parent channel ID for watchlist operations
              parentChannelId = null; // No parent needed since contentId is already the parent
            }
          } else if (channel?.resumption_data) {
            // Fallback: If this is a continue watching item (has resumption_data), use the appropriate channel ID
            if (channel.resumption_data.parent_channel) {
              channelId = channel.resumption_data.parent_channel._id;
            } else if (channel.resumption_data.channel) {
              channelId = channel.resumption_data.channel._id;
            }
          } else if (channel?.childchannels && channel.childchannels.length > 0) {
            // This is a series from AppLoad with childchannels
            // Use the first child channel as the channelId and the parent as parentChannelId
            channelId = channel.childchannels[0]._id;
            parentChannelId = channel._id;
          }

          // Use the reactive hook function instead of directly calling userDataService
          const isInList = hookIsInWatchlist(channelId, parentChannelId);
          setIsInWatchlist(isInList);
        } catch (error) {
          console.error('ThumbnailHover: Error checking watchlist status:', error);
        }
      }
    }, [isVisible, isAuthenticated, channel?._id, hookIsInWatchlist, watchlistData]); // Include watchlistData to trigger re-check when watchlist changes

    // Handle visibility changes
    useEffect(() => {
      if (isVisible) {
        // When modal becomes visible
        setShouldRender(true);
        setExiting(false);
      } else {
        // When modal should hide
        setExiting(true);

        // Clean up preview
        if (previewTimerRef.current) {
          clearTimeout(previewTimerRef.current);
          previewTimerRef.current = null;
        }

        // IMMEDIATELY stop video to prevent race condition with PDP modal
        // This ensures GlobalPreviewPlayer unmounts before PDP modal tries to use it
        setShowVideo(false);
        setPreviewUrl(null);

        // Wait for animation before fully unmounting
        const timer = setTimeout(() => {
          setShouldRender(false);
          setVideoLoaded(false);
          setIsInWatchlist(false);

          // Clean up video fade-in effect
          const videoElement = document.querySelector('.modal-video video');
          if (videoElement) {
            videoElement.classList.remove('playing');
          }
        }, 200); // match exit animation duration (0.2s)

        return () => clearTimeout(timer);
      }
    }, [isVisible]); // Only depend on visibility

    // Handle video timer separately to avoid re-renders
    useEffect(() => {
      if (isVisible && shouldRender && videoId) {
        // Start preview after hover delay
        const videoTimer = setTimeout(() => {
          setShowVideo(true);
        }, HOVER_DELAY);

        return () => {
          clearTimeout(videoTimer);
        };
      }
    }, [isVisible, shouldRender, videoId, HOVER_DELAY]); // Only depend on essential data

    if (!shouldRender) return null;

    // Render modal
    return (
      <div
        ref={modalRef}
        className={`thumbnail-hover-modal ${!exiting ? 'loaded' : 'exiting'} ${positionClass}`}
        style={{
          left: `${position?.x || 0}px`,
          top: `${position?.y || 0}px`,
        }}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {/* Media Container */}
        <div className="modal-media-container">
          {showVideo && previewUrl ? (
            <div className={`video-container ${videoLoaded ? 'loaded' : ''}`}>
              {(() => {
                // Only log and render if startTime actually changed
                const shouldLog = lastStartTimeRef.current !== startTime;
                if (shouldLog) {
                  lastStartTimeRef.current = startTime;
                }

                return (
                  <GlobalPreviewPlayer
                    key={`${previewUrl}-${videoId}`} // Use videoId instead of startTime for stable key
                    hlsUrl={previewUrl}
                    autoPlay={true}
                    muted={isMuted}
                    controls={false}
                    qualityMode="thumbnail"
                    className={`preview-player-thumbnail ${videoLoaded ? 'visible' : 'hidden'}`}
                    startTime={startTime}
                    onReady={handleVideoReady}
                    onError={handleVideoError}
                  />
                );
              })()}
              {!videoLoaded && (
                <img src={getImageUrl()} alt={title} className="fallback-thumbnail" />
              )}
            </div>
          ) : (
            <img src={getImageUrl()} alt={title} className="modal-image" />
          )}

          {/* Gradient Overlay */}
          <div className="modal-gradient-overlay" />

          {/* Volume Toggle Button - Bottom Right Corner */}
          <button
            className="modal-volume-button-bottom-right"
            onClick={handleVolumeToggle}
            title={isMuted ? 'Unmute' : 'Mute'}
          >
            {isMuted ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className="button-icon"
              >
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                <line x1="23" y1="9" x2="17" y2="15" />
                <line x1="17" y1="9" x2="23" y2="15" />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className="button-icon"
              >
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" />
              </svg>
            )}
          </button>

        </div>

        {/* Content */}
        <div className="modal-content">
          {/* Title */}
          <h3 className="modal-title">{title}</h3>

          {/* Metadata */}
          {(year || duration || programType || genres.length > 0) && (
            <div className="modal-metadata">
              {[year, duration, programType, genres.slice(0, 2).join(', ')]
                .filter(Boolean)
                .join(' · ')}
              
            </div>
          )}

          <div className="modal-pill-container">
            {rating && <div className="modal-pill">{rating}</div>}
            {language && <div className="modal-pill">{language}</div>}
            {country && <div className="modal-pill">{country}</div>}
          </div>
          {/* Description */}
          <p className="modal-description">
            {description.length > 80 ? `${description.substring(0, 80)}...` : description}
          </p>

          {/* Action Buttons */}
          <div className="modal-actions">
            {/* Left side: Resume + Watchlist buttons */}
            <div className="modal-actions-left">
              <button className={watchButtonInfo.className} onClick={handleWatchNow}>
                {watchButtonInfo.icon === 'lock' && (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="button-icon"
                  >
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                )}
                {watchButtonInfo.icon === 'play' && (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="button-icon"
                  >
                    <path d="M8 5v14l11-7z" />
                  </svg>
                )}
                {watchButtonInfo.icon === 'resume' && (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="button-icon"
                  >
                    <path d="M8 5v14l11-7z" />
                    <path d="M8 5v14l11-7z" />
                    <circle cx="12" cy="12" r="2" fill="#2f6eb0" />
                  </svg>
                )}
                {watchButtonInfo.icon === 'replay' && (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    className="button-icon"
                  >
                    <polyline points="1 4 1 10 7 10" />
                    <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                  </svg>
                )}
                {watchButtonInfo.text}
              </button>

              <button
                className={`modal-watchlist-button ${isInWatchlist ? 'in-watchlist' : ''}`}
                onClick={handleWatchlistToggle}
                disabled={isAddingToWatchlist}
                title={isInWatchlist ? strings.removeFromMyList : strings.addToMyList}
              >
                {isAddingToWatchlist ? (
                  <svg
                    className="animate-spin"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M21 12a9 9 0 11-6.219-8.56" />
                  </svg>
                ) : isInWatchlist ? (
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                    <line x1="12" y1="6" x2="12" y2="18" />
                    <line x1="6" y1="12" x2="18" y2="12" />
                  </svg>
                )}
              </button>
            </div>

            {/* Center: Reaction Buttons */}
            <ReactionButtons 
              channelId={channel?.dspro_id || channel?.contentId || channel?._id}
              key={channel?.dspro_id || channel?._id}
              initialReaction={channel?.user_reaction || channel?.reaction || null}
              size="medium"
              className="modal-reaction-buttons"
            />

            {/* Right side: More Info button */}
            <div className="modal-actions-right">

              <button
                className="modal-info-button"
                onClick={() => {
                  // Call the callback to open PDP modal
                  if (onOpenPDPModal) {
                    onOpenPDPModal({
                      autoPlay: false, // Let PDP modal handle its own preview
                      currentTime: 0,
                    });
                  }
                }}
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="button-icon"
                >
                  <path d="m6 9 6 6 6-6" />
                </svg>
                {showTooltip && <div className="modal-tooltip">{strings.moreInfo}</div>}
              </button>
            </div>
          </div>

          {/* Progress Bar - Show for content with watch progress */}
          {(() => {
            const hasWatchProgress =
              watchProgress && (watchProgress.progress > 0 || watchProgress.point > 0);
            const hasResumptionData =
              channel?.resumption_data &&
              (channel.resumption_data.progress > 0 || channel.resumption_data.point > 0);
            const shouldShowProgress = hasWatchProgress || hasResumptionData;

            // Get correct episode information for series in continue watching
            const getCorrectProgressData = () => {
              const baseData = watchProgress || channel?.resumption_data;
              if (!baseData) return null;

              // For series in continue watching, try to get correct episode info
              if (channel?.parent_channel_id) {
                try {
                  const seriesId = channel.parent_channel_id;
                  const continueWatchingInfo = episodeSelectionService.getContinueWatchingEpisode(
                    seriesId,
                    [],
                  );
                  if (continueWatchingInfo && continueWatchingInfo.episode) {
                    return {
                      ...baseData,
                      episodeNumber:
                        continueWatchingInfo.episode.episode_number ||
                        continueWatchingInfo.episode.episode ||
                        1,
                      seasonNumber:
                        continueWatchingInfo.season?.season_number ||
                        continueWatchingInfo.season?.season ||
                        1,
                    };
                  }
                } catch (error) {
                  console.error('ThumbnailHoverModal: Error getting correct episode info:', error);
                }
              }

              return baseData;
            };

            return shouldShowProgress ? (
              <ProgressBar
                progressData={getCorrectProgressData()}
                variant="modal"
                size="medium"
                showProgressBar={true}
                showEpisodeInfo={true}
              />
            ) : null;
          })()}
        </div>
      </div>
    );
  },
);

export default ThumbnailHoverModal;
